import React, { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { SITE, WA_DEFAULT, goTo } from "@/lib/site";

export const LogoMark = ({ className = "h-11 w-auto" }) => (
    <img
        src="/logo-mark.png"
        alt="GTECH Soluções Eletrônicas"
        className={className}
    />
);

const GoogleIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.6-.2-2.3H12v4.5h6.5c-.1 1-.8 2.6-2.4 3.7l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.8z" />
        <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5H1.4v3C3.4 21.4 7.4 24 12 24z" />
        <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4v-3H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.8-3z" />
        <path fill="#EA4335" d="M12 4.8c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.6l3.8 3c.9-2.9 3.6-4.8 6.8-4.8z" />
    </svg>
);

const LINKS = [
    { label: "Serviços", hash: "#servicos" },
    { label: "Simulador", hash: "#simulador" },
    { label: "Processo", hash: "#processo" },
    { label: "Trabalhos", hash: "#trabalhos" },
    { label: "FAQ", hash: "#faq" },
    { label: "Contato", hash: "#contato" },
];

const AuthArea = ({ mobile = false }) => {
    const { user, checking, login, logout } = useAuth();

    if (checking && !user) {
        return (
            <button
                data-testid={mobile ? "navbar-google-login-mobile" : "navbar-google-login"}
                disabled
                className={`items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-hud text-[11px] uppercase tracking-[0.18em] text-slate-400 opacity-60 ${mobile ? "flex w-full justify-center" : "hidden sm:inline-flex"}`}
            >
                <GoogleIcon />
                Entrar com Google
            </button>
        );
    }

    if (user) {
        return (
            <div className={`items-center gap-3 ${mobile ? "flex" : "hidden sm:flex"}`}>
                {user.picture ? (
                    <img
                        src={user.picture}
                        alt=""
                        referrerPolicy="no-referrer"
                        data-testid="navbar-user-avatar"
                        className="h-8 w-8 rounded-full border border-volt/40 object-cover"
                    />
                ) : (
                    <span data-testid="navbar-user-avatar" className="flex h-8 w-8 items-center justify-center rounded-full border border-volt/40 bg-volt/10 font-hud text-xs text-volt">
                        {user.name?.[0]?.toUpperCase() ?? "U"}
                    </span>
                )}
                <span className="font-hud text-[10px] uppercase tracking-[0.2em] text-slate-200 max-w-[110px] truncate">
                    {user.name}
                </span>
                <button
                    data-testid={mobile ? "navbar-logout-button-mobile" : "navbar-logout-button"}
                    onClick={logout}
                    className="rounded-full border border-white/20 px-4 py-2 font-hud text-[10px] uppercase tracking-[0.18em] text-slate-300 hover:border-volt hover:text-volt transition-colors duration-200"
                >
                    Sair
                </button>
            </div>
        );
    }

    return (
        <button
            data-testid={mobile ? "navbar-google-login-mobile" : "navbar-google-login"}
            onClick={login}
            className={`items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-hud text-[11px] font-bold uppercase tracking-[0.18em] whitespace-nowrap text-slate-100 hover:border-volt hover:text-volt transition-colors duration-200 ${mobile ? "flex w-full justify-center" : "hidden sm:inline-flex"}`}
        >
            <GoogleIcon />
            Entrar com Google
        </button>
    );
};

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar-container"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
                scrolled || open
                    ? "bg-ink/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-5 lg:px-8 h-[72px] flex items-center justify-between">
                <a
                    href="#"
                    data-testid="navbar-logo-link"
                    onClick={(e) => {
                        e.preventDefault();
                        if (window.__lenis) window.__lenis.scrollTo(0);
                        else window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex items-center gap-3"
                >
                    <LogoMark />
                    <span className="leading-none">
                        <span className="block font-display font-bold tracking-[0.22em] text-white text-lg">
                            {SITE.name}
                        </span>
                        <span className="block font-hud text-[9px] uppercase tracking-[0.3em] text-volt mt-1">
                            {SITE.tagline}
                        </span>
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <a
                            key={l.hash}
                            href={l.hash}
                            onClick={(e) => goTo(e, l.hash)}
                            className="font-hud text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:text-volt transition-colors duration-200"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={WA_DEFAULT}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="navbar-whatsapp-cta"
                        className="hidden md:inline-flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 font-hud text-[11px] font-bold uppercase tracking-[0.18em] whitespace-nowrap text-ink hover:bg-volt transition-colors duration-200"
                    >
                        <MessageCircle size={15} strokeWidth={2.4} />
                        Orçamento grátis
                    </a>
                    <AuthArea />
                    <button
                        data-testid="navbar-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden p-2 text-slate-200 hover:text-volt transition-colors duration-200"
                        aria-label="Abrir menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-ink/95 backdrop-blur-xl border-t border-white/10 px-5 pb-8 pt-4">
                    <div className="flex flex-col gap-1">
                        <AuthArea mobile />
                        {LINKS.map((l) => (
                            <a
                                key={l.hash}
                                href={l.hash}
                                onClick={(e) => {
                                    goTo(e, l.hash);
                                    setOpen(false);
                                }}
                                className="py-3 font-display text-2xl font-bold uppercase text-slate-100 hover:text-volt transition-colors duration-200"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href={WA_DEFAULT}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-mint px-6 py-3.5 font-hud text-xs font-bold uppercase tracking-[0.18em] text-ink"
                        >
                            <MessageCircle size={16} strokeWidth={2.4} />
                            Orçamento grátis no WhatsApp
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Nav;
